import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, CssBaseline, Paper, Toolbar } from "@mui/material";
import { Box } from "@mui/system";
import { TypographyLarge, TypographyXLarge } from "../customComponent";
import Sidebar from "../sidebar/Sidebar";
import UserSuccessPrompt from "./UserSuccessPrompt";
import UserErrorPrompt from "./UserErrorPrompt";
import UserList from "./UserList";
import UserFilter from "./UserFilter";
import CreateUser from "./CreateUser";
import Role from "../../helpers/roles";
import {
  SET_ERROR_MESSAGE_USER_PAGE,
  SET_ERROR_PROMPT_USER_PAGE,
  SET_ORGANIZATION_OBJECT,
  SET_USER_GROUP_OBJECT,
} from "../../store/actionTypes/userActionType";
import {
  API_LIMIT,
  initiateOrganizationAPI,
  initiateUserGroupAPI,
} from "../../api";
import { initiateAXIOS } from "../../config/axios";

const User = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("user");

  const { user, features } = useSelector((state) => state.auth);
  const userRole = user?.type;
  const permission = user?.permission;
  const userPermission = permission?.user;
  const userGroupPermission = permission?.userGroup;

  const [userGroups, setUserGroups] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const fetchOnFirstLoad = useRef(false);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    if (features?.["USER"] === false) {
      navigate("/");
      return;
    }

    if (!permission) {
      return;
    }

    if (userPermission?.read === false && userRole !== Role.Eins) {
      navigate("/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, features]);

  const getUserGroups = async () => {
    const access_token = sessionStorage.getItem("access_token");
    try {
      const { data } = await initiateAXIOS.get(
        initiateUserGroupAPI + `?limit=${API_LIMIT}`,
        {
          headers: { authorization: `Bearer ${access_token}` },
        }
      );

      setUserGroups(data.items);
      data.items?.forEach((item) => {
        dispatch({
          type: SET_USER_GROUP_OBJECT,
          payload: {
            id: item.id,
            object: item,
          },
        });
      });
    } catch (error) {
      const errorMessage = error.response.data.message;
      dispatch({ type: SET_ERROR_PROMPT_USER_PAGE, payload: true });
      dispatch({ type: SET_ERROR_MESSAGE_USER_PAGE, payload: errorMessage });
    }
  };

  const getOrganizations = async () => {
    const access_token = sessionStorage.getItem("access_token");
    try {
      const { data } = await initiateAXIOS.get(
        initiateOrganizationAPI + `?limit=${API_LIMIT}`,
        {
          headers: { authorization: `Bearer ${access_token}` },
        }
      );

      setOrganizations(data.items);
      data.items?.forEach((item) => {
        dispatch({
          type: SET_ORGANIZATION_OBJECT,
          payload: {
            id: item.id,
            object: item,
          },
        });
      });
    } catch (error) {
      const errorMessage = error.response.data.message;
      dispatch({ type: SET_ERROR_PROMPT_USER_PAGE, payload: true });
      dispatch({ type: SET_ERROR_MESSAGE_USER_PAGE, payload: errorMessage });
    }
  };

  const fetchSupportingData = () => {
    if (userGroupPermission?.read) getUserGroups();
    if (userRole === Role.Eins) getOrganizations();
  };

  useEffect(() => {
    if (user.id && !fetchOnFirstLoad.current) {
      fetchSupportingData();
      fetchOnFirstLoad.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!features?.enabled) {
    return (
      <div style={{ display: "flex" }}>
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1, p: 1 }}></Box>
      </div>
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />

      <Box component="main" sx={{ flexGrow: 1, p: 1 }}>
        <Toolbar />
        <CssBaseline />

        <Container maxWidth="xl">
          <Box sx={{ mb: 1 }}>
            <TypographyXLarge sx={{ fontWeight: 700 }}>
              {t("title")}
            </TypographyXLarge>
            <TypographyLarge sx={{ fontWeight: 400 }}>
              {t("titleExplain")}
            </TypographyLarge>
          </Box>

          <UserSuccessPrompt />
          <UserErrorPrompt />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {(userRole !== Role.User || userPermission?.create) && (
              <CreateUser />
            )}
          </Box>

          <Paper
            component="section"
            elevation={2}
            sx={{ p: 2, my: 2, borderRadius: "6px" }}
          >
            <UserFilter
              userGroups={userGroups}
              organizations={organizations}
              fetchSupportingData={fetchSupportingData}
            />
          </Paper>

          <UserList />
        </Container>
      </Box>
    </div>
  );
};

export default User;
